import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MarketingNav } from "../components/Nav";
import Footer from "../components/Footer";
import InquiryModal from "../components/InquiryModal";
import { getToken } from "../lib/auth";

const prices = [
  {
    id: "free",
    tagline: "Start here",
    name: "Free",
    price: "$0",
    period: "forever",
    featured: false,
    cta: "Continue free",
    features: [
      "Full Select Seven journey",
      "Head-to-head rounds",
      "Compatibility scoring",
      "Winner reveal",
    ],
  },
  {
    id: "plus",
    tagline: "Most chosen",
    name: "Plus",
    price: "$19",
    period: "/ month",
    featured: true,
    cta: "Start Plus",
    features: [
      "Everything in Free",
      "Unlimited rounds",
      "Priority matching",
      "Read receipts",
      "Advanced compatibility insights",
    ],
  },
  {
    id: "private",
    tagline: "By invitation",
    name: "Private Table",
    price: "Apply",
    period: "",
    featured: false,
    cta: "Apply for access",
    features: [
      "Everything in Plus",
      "Concierge matchmaking",
      "Hand-curated Select Seven",
      "Dedicated advisor",
      "Seasonal limited seats",
    ],
  },
];

const PRIVATE_TABLE_FIELDS = [
  { name: "fullName",  label: "Full name",        type: "text",     placeholder: "First and last name",           required: true },
  { name: "email",     label: "Email address",     type: "email",    placeholder: "you@example.com",              required: true },
  { name: "city",      label: "City / location",   type: "text",     placeholder: "e.g. London, New York, Lagos", required: true },
  {
    name: "why",
    label: "Why do you want an invitation?",
    type: "textarea",
    placeholder: "Tell us a little about what you're looking for and why BoraMoyo Private feels right for you.",
    required: true,
    rows: 4,
  },
  { name: "social", label: "Profile or social link (optional)", type: "url", placeholder: "https://linkedin.com/in/…", required: false },
];

export default function Membership() {
  const [applyOpen,       setApplyOpen]       = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [checkoutError,   setCheckoutError]   = useState("");
  const [freeLoading,     setFreeLoading]     = useState(false);
  const [searchParams]                        = useSearchParams();
  const checkoutStatus                        = searchParams.get("checkout");

  const handlePrivateApply = async (formData) => {
    console.log("PRIVATE_APPLY_HANDLER_FIRED", formData);
    alert("Private apply handler fired");
    const token = getToken();
    const res = await fetch("/api/private/apply", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(formData),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.detail || "Submission failed");
  };

  const chooseFree = async () => {
    const token = getToken();
    setFreeLoading(true);
    setCheckoutError("");
    try {
      if (token) {
        await fetch("/api/me/subscription/free", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      window.location.href = "/dashboard";
    } catch {
      window.location.href = "/dashboard";
    }
  };

  const startCheckout = async (plan) => {
    const token = getToken();
    if (!token) { window.location.href = "/login?next=/membership"; return; }
    setCheckoutLoading(plan);
    setCheckoutError("");
    try {
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Could not start checkout");
      window.location.href = d.checkout_url;
    } catch (e) {
      setCheckoutError(e.message);
      setCheckoutLoading("");
    }
  };

  return (
    <div className="bg-gradient-hero min-h-screen text-white">
      <MarketingNav />

      {checkoutStatus === "success" && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 glass-strong rounded-2xl px-6 py-4 text-emerald-400 text-sm font-medium shadow-xl flex items-center gap-4">
          <span>✓ Subscription activated — welcome to {searchParams.get("plan") || "your new plan"}!</span>
          <a href="/dashboard" className="rounded-full bg-gradient-gold text-black text-xs font-semibold px-4 py-1.5 whitespace-nowrap">
            Continue to dashboard →
          </a>
        </div>
      )}
      {checkoutStatus === "canceled" && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 glass-strong rounded-2xl px-6 py-4 text-white/60 text-sm shadow-xl">
          Checkout canceled — no charge was made.
        </div>
      )}
      {checkoutError && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 glass-strong rounded-2xl px-6 py-4 text-red-400 text-sm shadow-xl">
          {checkoutError}
        </div>
      )}

      <section className="pt-40 pb-16 md:pt-48">
        <div className="mx-auto max-w-6xl px-6 md:px-12 text-center">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#F2DE94] mb-4">
            Membership · four tiers, no theatre
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.02]">
            Choose your <span className="italic text-gradient-gold">table.</span>
          </h1>
          <p className="text-white/60 max-w-xl mx-auto mt-5 text-lg">
            Every journey is real on every tier. These just choose the depth.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-6xl px-6 md:px-12 grid md:grid-cols-3 gap-5">
          {prices.map((t) => (
            <div
              key={t.id}
              data-testid={`price-${t.id}`}
              className={`relative rounded-3xl p-8 transition-all ${
                t.featured
                  ? "glass-strong ring-gold glow-gold-soft"
                  : "glass hover:-translate-y-1"
              }`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-8">
                  <span className="rounded-full bg-gradient-gold text-[#05070d] text-[10px] uppercase tracking-[0.28em] px-3 py-1 font-semibold">
                    Most chosen
                  </span>
                </div>
              )}
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37]">
                {t.tagline}
              </div>
              <div className="font-display text-3xl mt-2">{t.name}</div>
              <div className="mt-5 flex items-baseline gap-2">
                <div className={`font-display text-5xl ${t.featured ? "text-gradient-gold" : ""}`}>
                  {t.price}
                </div>
                <div className="text-white/50 text-sm">{t.period}</div>
              </div>
              <div className="hairline my-6" />
              <ul className="space-y-2.5 text-sm text-white/80">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-3 items-start">
                    <span className="mt-1 text-[#D4AF37]">
                      <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current">
                        <path d="M7.6 14.2 3.4 10l1.4-1.4 2.8 2.8 7-7L16 5.8z" />
                      </svg>
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`price-cta-${t.id}`}
                disabled={checkoutLoading === t.id}
                onClick={() => {
                  if (t.id === "free") { chooseFree(); return; }
                  if (t.id === "private") { setApplyOpen(true); return; }
                  startCheckout(t.id);
                }}
                disabled={checkoutLoading === t.id || (t.id === "free" && freeLoading)}
                className={`mt-8 w-full rounded-full font-semibold py-3.5 transition-all disabled:opacity-50 ${
                  t.featured
                    ? "bg-gradient-gold text-[#05070d] glow-gold"
                    : "bg-white/5 hover:bg-white/10 text-white/90 border border-white/10"
                } cursor-pointer`}
              >
                {(t.id === "free" && freeLoading) ? "Setting up…" : checkoutLoading === t.id ? "Redirecting…" : t.cta}
              </button>
            </div>
          ))}
        </div>

        <div id="concierge" className="mx-auto max-w-4xl px-6 md:px-12 mt-16 scroll-mt-24">
          <div className="glass rounded-3xl p-8 md:p-10 text-center">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
              For the truly specific
            </div>
            <h3 className="font-display text-3xl md:text-4xl">
              Private Matchmaking · by invitation
            </h3>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">
              A concierge hand-curates your seven. Real humans, premium research,
              scheduled intros. Limited seats per season.
            </p>
            <button
              data-testid="apply-concierge"
              onClick={() => setApplyOpen(true)}
              className="mt-6 rounded-full border border-[rgba(212,175,55,0.45)] text-[#F2DE94] px-6 py-3 text-sm hover:bg-[rgba(212,175,55,0.08)] transition-colors"
            >
              Apply for invitation →
            </button>
          </div>
        </div>
      </section>

      <InquiryModal
        isOpen={applyOpen}
        onClose={() => setApplyOpen(false)}
        title="Apply for Private Matchmaking"
        subtitle="Limited seats per season. We review every application personally."
        fields={PRIVATE_TABLE_FIELDS}
        submitLabel="Submit application"
        onSubmit={handlePrivateApply}
      />

      <Footer />
    </div>
  );
}
